import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TeamKnowledgeWebsiteDTO } from "@shaarp/common";
import { ColumnDef } from "@tanstack/react-table";
import {
  ChevronDownIcon,
  CircleCheck,
  CircleX,
  GlobeIcon,
  RefreshCw,
  Trash2,
} from "lucide-react";
import DataTableCellTeamKnowledgeWebsitesCreatedBy from "./data-table-cell-team-knowledge-websites-created-by";

export const dataTableTeamKnowledgeWebsitesColumns = (
  t: (key: string) => string,
  onDelete: (website: TeamKnowledgeWebsiteDTO) => void,
  onRefresh: (website: TeamKnowledgeWebsiteDTO) => void
): ColumnDef<TeamKnowledgeWebsiteDTO>[] => {
  return [
    {
      id: "url",
      accessorKey: "url",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("team.knowledge.dataTable.columns.url")}
        />
      ),
      cell: ({ row }) => {
        const website: TeamKnowledgeWebsiteDTO = row.original;
        return (
          <a
            className="flex gap-2 items-center"
            href={website.url}
            target="_blank"
          >
            <GlobeIcon className="size-4! text-muted-foreground" />
            <span className="font-medium">{website.url}</span>
          </a>
        );
      },
    },
    {
      id: "createdBy",
      accessorKey: "createdBy",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("team.knowledge.dataTable.columns.createdBy")}
        />
      ),
      cell: ({ row }) => {
        const website: TeamKnowledgeWebsiteDTO = row.original;
        return (
          <DataTableCellTeamKnowledgeWebsitesCreatedBy website={website} />
        );
      },
    },
    {
      id: "refreshedAt",
      accessorKey: "refreshedAt",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("team.knowledge.dataTable.columns.refreshedAt")}
        />
      ),
      cell: ({ row }) => {
        const website: TeamKnowledgeWebsiteDTO = row.original;
        return (
          <span className="text-muted-foreground">
            {new Date(website.refreshedAt).toLocaleString()}
          </span>
        );
      },
    },
    {
      id: "error",
      accessorKey: "errorMessage",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("team.knowledge.dataTable.columns.error")}
        />
      ),
      cell: ({ row }) => {
        const website: TeamKnowledgeWebsiteDTO = row.original;
        return (
          <span className="text-muted-foreground">
            {website.errorMessage ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <CircleX className="size-4 text-destructive" />
                </TooltipTrigger>
                <TooltipContent>
                  {t("team.knowledge.dataTable.errorMessage")}
                </TooltipContent>
              </Tooltip>
            ) : (
              <CircleCheck className="text-green-500 size-4" />
            )}
          </span>
        );
      },
    },
    ...(onDelete || onRefresh
      ? [
          {
            id: "actions",
            header: "",
            cell: ({ row }: { row: any }) => {
              const website: TeamKnowledgeWebsiteDTO = row.original;
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      {t("team.knowledge.dataTable.actions.actions")}
                      <ChevronDownIcon className="ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onRefresh(website)}>
                      <RefreshCw className="mr-2 size-4" />
                      {t("team.knowledge.dataTable.actions.refresh")}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() => onDelete(website)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 size-4" />
                      {t("team.knowledge.dataTable.actions.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
            enableSorting: false,
          } as ColumnDef<TeamKnowledgeWebsiteDTO>,
        ]
      : []),
  ];
};
